import { c as _c } from "react/compiler-runtime";
import { css, cx } from '@repo/panda/css';
import { type ReactNode, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react';

/**
 * The bar floats over the map, so wherever it sits by default it can cover the pins being
 * worked on. It is dragged by its own surface; buttons keep their clicks.
 *
 * The position is module-wide rather than per-component on purpose: select, then move, is one
 * job to a creator, and the bar should not jump back to the middle of the screen between the two.
 */
let sharedOffset = {
  x: 0,
  y: 0
};

/** Keeps a bit of the bar on screen no matter where it is dropped, or the window is resized to. */
const VIEWPORT_MARGIN = 8;
const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), Math.max(low, high));
interface DragState {
  pointerId: number;
  pointerX: number;
  pointerY: number;
  offsetX: number;
  offsetY: number;
}
export const DraggableFloatingBar = t0 => {
  const $ = _c(18);
  const {
    children,
    className
  } = t0;
  const [offset, setOffset] = useState(sharedOffset);
  const [isDragging, setIsDragging] = useState(false);
  const elementRef = useRef(null);
  const dragRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      const element = elementRef.current;
      if (!element) {
        return;
      }
      setOffset(current => {
        const rect = element.getBoundingClientRect();
        const baseLeft = rect.left - current.x;
        const baseTop = rect.top - current.y;
        const wanted = next(current);
        const clamped = {
          x: clamp(wanted.x, VIEWPORT_MARGIN - baseLeft, window.innerWidth - VIEWPORT_MARGIN - rect.width - baseLeft),
          y: clamp(wanted.y, VIEWPORT_MARGIN - baseTop, window.innerHeight - VIEWPORT_MARGIN - rect.height - baseTop)
        };
        if (clamped.x === current.x && clamped.y === current.y) {
          return current;
        }
        sharedOffset = clamped;
        return clamped;
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const applyOffset = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const keepInView = () => applyOffset(_temp);
      keepInView();
      window.addEventListener("resize", keepInView);
      return () => window.removeEventListener("resize", keepInView);
    };
    t3 = [applyOffset];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== offset.x || $[4] !== offset.y) {
    t4 = event => {
      if (event.button !== 0) {
        return;
      }
      if ((event.target as HTMLElement).closest("button, a, input, select, textarea")) {
        return;
      }
      const element_0 = elementRef.current;
      if (!element_0) {
        return;
      }
      dragRef.current = {
        pointerId: event.pointerId,
        pointerX: event.clientX,
        pointerY: event.clientY,
        offsetX: offset.x,
        offsetY: offset.y
      };
      element_0.setPointerCapture(event.pointerId);
      setIsDragging(true);
      event.preventDefault();
    };
    $[3] = offset.x;
    $[4] = offset.y;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handlePointerDown = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event_0 => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event_0.pointerId) {
        return;
      }
      applyOffset(() => ({
        x: drag.offsetX + event_0.clientX - drag.pointerX,
        y: drag.offsetY + event_0.clientY - drag.pointerY
      }));
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handlePointerMove = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event_1 => {
      const drag_0 = dragRef.current;
      if (!drag_0 || drag_0.pointerId !== event_1.pointerId) {
        return;
      }
      elementRef.current?.releasePointerCapture(event_1.pointerId);
      dragRef.current = null;
      setIsDragging(false);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handlePointerEnd = t6;
  const t7 = `translate(calc(-50% + ${offset.x}px), ${offset.y}px)`;
  const t8 = isDragging ? "grabbing" : "grab";
  let t9;
  if ($[8] !== t7 || $[9] !== t8) {
    t9 = {
      transform: t7,
      cursor: t8
    };
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== className) {
    t10 = cx(css({
      position: "absolute",
      bottom: "96",
      left: "[50%]",
      zIndex: 3,
      width: "[min(420px, calc(100vw - 32px))]",
      backgroundColor: "blackAlpha.80",
      border: "[1px solid]",
      borderColor: "whiteAlpha.10",
      borderRadius: "8",
      boxShadow: "xl",
      padding: "12",
      pointerEvents: "auto",
      userSelect: "none",
      touchAction: "none"
    }), className);
    $[11] = className;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== children || $[14] !== handlePointerDown || $[15] !== t10 || $[16] !== t9) {
    t11 = <div ref={elementRef} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerEnd} onPointerCancel={handlePointerEnd} style={t9} className={t10}>{children}</div>;
    $[13] = children;
    $[14] = handlePointerDown;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
};
function _temp(current_0) {
  return current_0;
}